import React from 'react'

type Glyph = { x: number; y: number; scale: number; strokeWidth: number }

type Weight = {
  size: [number, number]
  frame: number
  radius: number
  fish: Glyph
  dragonfly: Glyph
  bacterium: Glyph
  bars: { x: number; y: number; height: number; unit: number; pattern: number[] }
}

// One drawing at two weights: thin lines merge at 32px, thick ones turn heavy at display size.
const WEIGHTS: Record<'icon' | 'display', Weight> = {
  icon: {
    size: [360, 240],
    frame: 20,
    radius: 32,
    fish: { x: 85, y: 84, scale: 1.05, strokeWidth: 11.43 },
    dragonfly: { x: 180, y: 84, scale: 1.1, strokeWidth: 10.88 },
    bacterium: { x: 275, y: 84, scale: 0.966, strokeWidth: 12.42 },
    bars: { x: 70, y: 144, height: 50, unit: 10, pattern: [1, 2, 1, 3, 1, 2, 1, 1, 2] }
  },
  display: {
    size: [354, 234],
    frame: 7,
    radius: 18,
    fish: { x: 78, y: 78, scale: 0.85, strokeWidth: 8.24 },
    dragonfly: { x: 177, y: 78, scale: 0.9, strokeWidth: 7.78 },
    bacterium: { x: 276, y: 78, scale: 0.78, strokeWidth: 8.97 },
    bars: { x: 66, y: 140, height: 54, unit: 6, pattern: [1, 2, 1, 1, 3, 1, 2, 1, 1, 3, 2, 1, 1, 2, 1] }
  }
}

const barRects = ({ x, y, height, unit, pattern }: Weight['bars']) => {
  let cx = x
  return pattern.map((w) => {
    const rect = <rect key={cx} x={cx} y={y} width={w * unit} height={height} rx={unit / 2} fill="currentColor" stroke="none" />
    cx += (w + 1) * unit
    return rect
  })
}

const at = (g: Glyph, rotate = 0) => ({
  transform: `translate(${g.x} ${g.y}) rotate(${rotate}) scale(${g.scale})`,
  strokeWidth: g.strokeWidth
})

/** SPIN identity card: a fish, a dragonfly and a bacterium above a barcode. `icon` for ~32px slots, `display` for larger. */
export const SpinIdCardIcon = ({ className, variant = 'icon' }: { className?: string; variant?: 'icon' | 'display' }) => {
  const w = WEIGHTS[variant]
  const [width, height] = w.size
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={w.frame}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x={w.frame / 2} y={w.frame / 2} width={width - w.frame} height={height - w.frame} rx={w.radius} />
      <g {...at(w.fish)}>
        <path d="M-20 0c7-10 17-15 29-12 7 2 12 6 15 12-3 6-8 10-15 12-12 3-22-2-29-12Z" />
        <path d="M-20 0-31-11M-20 0-31 11M-31-11c4 7 4 15 0 22" />
        <circle cx={14} cy={-2.5} r={2.5} fill="currentColor" stroke="none" />
      </g>
      <g {...at(w.dragonfly)}>
        <circle cy={-24} r={6} />
        <path d="M0-18v50" />
        <path d="M-4-12c-14-6-32-4-32 4s18 8 32 2M4-12c14-6 32-4 32 4s-18 8-32 2" />
        <path d="M-4 2c-12-4-28-2-28 5s16 8 28 2M4 2c12-4 28-2 28 5s-16 8-28 2" />
      </g>
      <g {...at(w.bacterium, 25)}>
        <path d="M11.3 -19.7A74 74 0 0 1 11.3 19.7A14 14 0 0 1 -15.7 12.3A46 46 0 0 0 -15.7 -12.3A14 14 0 0 1 11.3 -19.7Z" />
        <circle cx={-3} cy={-18.9} r={3.5} fill="currentColor" stroke="none" />
        <path d="M13 -12.4L19.9 -13.5M14 2.6L21 2.8M11.9 17.4L18.7 19M6.3 27.2L10.5 32.7M-4.1 29.9L-5 36.8M-13.2 24.6L-18.8 28.9M-16.1 14.2L-23 13.4M-14.1 -3.2L-21.1 -2.7M-16 -18L-23 -19M-10.6 -27.2L-14.9 -32.7M-0.3 -29.9L0.6 -36.8M8.9 -24.6L14.4 -28.9" />
      </g>
      {barRects(w.bars)}
    </svg>
  )
}
